import React from 'react';
import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { AppBar, Dropdown, Icon } from '@brighterion/br-ui';
import { useLocation, useHistory, matchPath } from 'react-router-dom';
import { replaceUserRuleSetId, RoleService } from 'Services';
import { useSelector } from 'react-redux';
import { getAppUrls, getUser } from 'Selectors';
import { ROUTES_TO_HIDE_NAV } from 'Components/Breadcrumbs';
import { useAuthenticationStore } from 'Hooks/zustand/useAuthenticationStore';
import { useTemplateStore } from 'Hooks/zustand/useTemplateStore';
import { useTranslation } from 'react-i18next';
import { handleOnClickEvent, handleKeyEvent } from 'Utils';
import type { AppUrl } from '@brighterion/types/config';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import useGetManifest from 'Hooks/query-hooks/useGetManifest';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import MenuTab, { TABS } from './MenuTab';
import SubNav from './SubNav';
import ImpersonationHeader from '../ImpersonationHeader';
import { RuleSetSwitcher } from './RuleSetSwitcher';

import styles from './Navigation.css';

function AppSwitcher({ appUrls }: { appUrls: AppUrl[] }) {
    const { t } = useTranslation();
    const filteredAppUrls = appUrls?.filter((currentElement) => {
        if (
            (currentElement.text === 'Case Management' && !RoleService.hasAnyCmsRole) ||
            (currentElement.text === 'Tenancy Management' && !RoleService.hasAnyTmRole)
        )
            return false;
        return true;
    });

    if (filteredAppUrls?.length === 0) return null;

    return (
        <AppBar.IconDropdown>
            {filteredAppUrls?.map(({ text, url }) => {
                return (
                    <AppBar.IconDropdownMenuItem
                        key={url}
                        className={styles.appLink}
                        as='a'
                        target='_blank'
                        text={t(text)}
                        href={url}
                    />
                );
            })}
        </AppBar.IconDropdown>
    );
}

type NavigationProps = {
    options: {
        isMastercardConnectUser: boolean;
    };
};

const Navigation: React.FC<NavigationProps> = ({ options }) => {
    const { isMastercardConnectUser } = options;
    const { logout, switchRuleSet } = useAuthenticationStore();
    const { template } = useTemplateStore();
    const { clearTemplate } = useTemplateStore();
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const { addModal } = useReduxModal();
    const manifest = useGetManifest();
    const queryClient = useQueryClient();

    const shouldBeHidden = ROUTES_TO_HIDE_NAV.some((route) => {
        return !!matchPath(location.pathname, {
            path: route,
            exact: true
        });
    });

    const appUrls = useSelector(getAppUrls);
    const { username, landlordId, tenantKey } = useSelector(getUser);
    if (shouldBeHidden) return null;

    const NavTabs = [...TABS(RoleService, landlordId, tenantKey)];
    const activeRoute = NavTabs.find(({ route }) => location.pathname.includes(route));
    const showActiveRouteSubNav = activeRoute?.showFeature;

    const handleRuleSetSwitch = async (ruleSet: RuleSet) => {
        replaceUserRuleSetId(ruleSet);
        clearTemplate();
        await switchRuleSet();
        await queryClient.invalidateQueries();
        const search = new URLSearchParams({ flow: 'switch', name: ruleSet.displayName }).toString();
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Multirulesets - Switch Ruleset',
                text: 'Switch Ruleset'
            }
        });
        history.replace({ pathname: '/', search });
    };

    const isImpersonationMode = landlordId && landlordId !== tenantKey;

    const handleManifest = () => {
        if (manifest.isLoading) return null;
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Rules Management - View Info',
                text: 'View Rules Management Release Version'
            }
        });
        return addModal({
            id: 'about-modal',
            status: 'info',
            title: t(manifest.data?.short_name),
            cancelText: t('Close'),
            content: (
                <div className={styles.manifestContent}>
                    <div>
                        <h5 className={styles.manifestHeading}>{t('Service name')}</h5>
                        <p className={styles.manifestText}>{manifest.data?.short_name}</p>
                    </div>
                    <div>
                        <h5 className={styles.manifestHeading}>{t('Release version')}</h5>
                        <p className={styles.manifestText}>24RM1</p>
                    </div>
                    <div>
                        <p className={styles.manifestText}>{t('COPYRIGHT')}</p>
                    </div>
                </div>
            )
        });
    };

    return (
        <AppBar>
            {isImpersonationMode && <ImpersonationHeader />}
            <AppBar.Container>
                {!isMastercardConnectUser && (
                    <AppBar.LogoContainer
                        onContainerClick={() => {
                            history.push('/');
                        }}
                    >
                        <img
                            height={47}
                            width={123}
                            style={{ margin: '0 -10px' }}
                            alt='Brighterion, a Mastercard company'
                            src='/assets/img/mastercard-logo.svg'
                        />
                    </AppBar.LogoContainer>
                )}
                <AppBar.LeftContainer>
                    {NavTabs.map(({ route, displayName, showFeature }) => {
                        if (!showFeature) return null;
                        return (
                            <MenuTab
                                key={route}
                                route={route}
                                displayName={t(displayName)}
                                isActive={activeRoute?.route === route}
                                onClick={() => history.push(route)}
                            />
                        );
                    })}
                </AppBar.LeftContainer>
                <AppBar.RightContainer>
                    <div className={styles.appNameContainer}>
                        <span className={clsx(styles.appNameContent, appUrls?.length && styles.borderRight)}>
                            Rules Management
                        </span>
                    </div>
                    <div>
                        {template.ruleSets && (
                            <RuleSetSwitcher ruleSets={template.ruleSets} handleRuleSetSwitch={handleRuleSetSwitch} />
                        )}
                    </div>
                    <div className={styles.manifestContainer}>
                        <button type='button' className={styles.manifestButton} onClick={handleManifest}>
                            <Icon name='info' className={styles.manifestIcon} />
                        </button>
                    </div>
                    <AppSwitcher appUrls={appUrls} />
                    {!isMastercardConnectUser ? (
                        <Dropdown
                            id='avatar'
                            showArrows={false}
                            menuAlignment='right'
                            containerClassName={styles.avatarContainer}
                            menuDropdownClassName={styles.menuDropdown}
                        >
                            <>
                                <Icon className={styles.avatar} name='avatar' />
                            </>
                            <div tabIndex={-1} className={styles.displayOnly}>{`Logged in as ${username}`}</div>
                            <button
                                type='button'
                                className={styles.buttonGhost}
                                onClick={handleOnClickEvent(logout)}
                                onKeyDown={handleKeyEvent(logout)}
                            >
                                Log out
                            </button>
                        </Dropdown>
                    ) : null}
                </AppBar.RightContainer>
            </AppBar.Container>
            {showActiveRouteSubNav && <SubNav />}
        </AppBar>
    );
};

export default Navigation;
